import { openDatabase, parseReference, parseWorkspace, referenceFromWorkspace, type Reference, type Workspace } from "./workspace";

export type LibraryKind = "roast" | "profile";
export type LibrarySummary = {id: string; kind: LibraryKind; name: string; revision: number; updatedAt: number; provenance: "synthetic" | "csv" | "device"};
export type LibraryItem = {summary: LibrarySummary} & ({kind: "roast"; data: Workspace} | {kind: "profile"; data: Reference});
const MAX_ITEMS = 100;
const MAX_VERSIONS = 25;

function summary(value: unknown): LibrarySummary {
  if (!value || typeof value !== "object") throw new Error("Invalid local library entry.");
  const row = value as LibrarySummary;
  if (typeof row.id !== "string" || !row.id.trim() || row.id.length > 100 ||
    typeof row.name !== "string" || !row.name.trim() || row.name.length > 160 ||
    !["roast", "profile"].includes(row.kind) || !["synthetic", "csv", "device"].includes(row.provenance) ||
    !Number.isSafeInteger(row.revision) || row.revision < 1 || row.revision > MAX_VERSIONS ||
    typeof row.updatedAt !== "number" || row.updatedAt < 0 || !Number.isFinite(new Date(row.updatedAt).getTime())) {
    throw new Error("A local library entry is damaged. Existing records were preserved.");
  }
  return {id: row.id, kind: row.kind, name: row.name, revision: row.revision, updatedAt: row.updatedAt, provenance: row.provenance};
}

function requestValue<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Local library read failed."));
  });
}

/** Read bounded metadata only. Curves are loaded individually when opened. */
export async function listLibrary(): Promise<LibrarySummary[]> {
  const db = await openDatabase();
  try {
    const rows = await requestValue(db.transaction("libraryIndex").objectStore("libraryIndex").getAll(undefined, MAX_ITEMS + 1));
    if (rows.length > MAX_ITEMS) throw new Error("The local library exceeds its supported size.");
    return rows.map(summary).sort((a, b) => b.updatedAt - a.updatedAt || a.name.localeCompare(b.name));
  } finally { db.close(); }
}

export async function readLibrary(id: string, revision: number): Promise<LibraryItem> {
  if (!Number.isSafeInteger(revision) || revision < 1 || revision > MAX_VERSIONS) throw new Error("Choose a valid saved version.");
  const db = await openDatabase();
  try {
    const raw = await requestValue(db.transaction("library").objectStore("library").get([id, revision]));
    if (!raw) throw new Error("This saved version is no longer in the local library.");
    const meta = summary(raw.summary);
    if (meta.id !== id || meta.revision !== revision) throw new Error("Saved version identity does not match the library.");
    if (meta.kind === "roast") {
      const data = parseWorkspace(raw.data);
      if (meta.provenance !== data.source.provenance) throw new Error("Saved source does not match its library label.");
      return {summary: meta, kind: "roast", data};
    }
    const reference = parseReference(raw.data);
    if (reference.profile?.id !== id || reference.profile.revision !== revision) throw new Error("Saved reference identity does not match its version.");
    if (meta.provenance !== reference.provenance || meta.name !== reference.name) throw new Error("Saved reference does not match its library label.");
    return {summary: meta, kind: "profile", data: reference};
  } finally { db.close(); }
}

/** Each version is append-only; the index and snapshot commit together. */
export async function saveLibrary(input: {kind: LibraryKind; name: string; workspace: Workspace; target?: {id: string; revision: number}}): Promise<LibrarySummary> {
  const workspace = parseWorkspace(input.workspace);
  const name = input.name.trim();
  if (!name || name.length > 160) throw new Error("Name this item using 1–160 characters.");
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(["library", "libraryIndex"], "readwrite");
      const index = transaction.objectStore("libraryIndex");
      let result: LibrarySummary;
      let failure: Error | null = null;
      const fail = (error: unknown) => { failure = error instanceof Error ? error : new Error("Local library save failed."); transaction.abort(); };
      const write = (previous: LibrarySummary | null) => {
        try {
          const revision = previous ? previous.revision + 1 : 1;
          if (revision > MAX_VERSIONS) throw new Error("This item has 25 versions. Save a new named item to preserve more versions.");
          result = {id: previous?.id ?? crypto.randomUUID(), kind: input.kind, name, revision, updatedAt: Date.now(), provenance: workspace.source.provenance};
          const data = input.kind === "roast" ? workspace : {...referenceFromWorkspace(workspace, name), profile: {id: result.id, revision}};
          // add() also rejects an unexpected existing version rather than overwriting it.
          transaction.objectStore("library").add({summary: result, data}, [result.id, revision]);
          index.put(result, result.id);
        } catch (error) { fail(error); }
      };
      if (input.target) {
        const request = index.get(input.target.id);
        request.onsuccess = () => {
          try {
            if (!request.result) throw new Error("This library item was removed. Save a new item instead.");
            const previous = summary(request.result);
            if (previous.id !== input.target!.id) throw new Error("Saved item identity does not match its library entry.");
            if (previous.kind !== input.kind || previous.revision !== input.target!.revision) throw new Error("Another tab changed this item. Reload the library before adding a version.");
            write(previous);
          } catch (error) { fail(error); }
        };
      } else {
        const request = index.count();
        request.onsuccess = () => {
          if (request.result >= MAX_ITEMS) fail(new Error("The local library holds 100 items. Export and remove an item before adding another."));
          else write(null);
        };
      }
      transaction.oncomplete = () => resolve(result!);
      transaction.onabort = () => reject(failure ?? transaction.error ?? new Error("Local library save was interrupted."));
      transaction.onerror = () => reject(transaction.error ?? new Error("Local library save failed."));
    });
  } finally { db.close(); }
}

export async function removeLibrary(item: LibrarySummary): Promise<void> {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(["library", "libraryIndex"], "readwrite");
      let failure: Error | null = null;
      const index = transaction.objectStore("libraryIndex");
      const request = index.get(item.id);
      request.onsuccess = () => {
        try {
          if (!request.result) throw new Error("This library item was already removed.");
          const current = summary(request.result);
          if (current.id !== item.id || current.kind !== item.kind) throw new Error("Saved item identity does not match its library entry.");
          if (current.revision !== item.revision) throw new Error("Another tab added a version. Reload the library before removing this item.");
          transaction.objectStore("library").delete(IDBKeyRange.bound([item.id, 1], [item.id, MAX_VERSIONS]));
          index.delete(item.id);
        } catch (error) { failure = error instanceof Error ? error : new Error("Could not remove this item."); transaction.abort(); }
      };
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(failure ?? transaction.error ?? new Error("Removal was interrupted."));
      transaction.onerror = () => reject(transaction.error ?? new Error("Could not remove this item."));
    });
  } finally { db.close(); }
}
